if (!customElements.get('influencer-chef')) {
  customElements.define(
    'influencer-chef',
    class InfluencerChef extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-influencer-list]');
        this.cards = Array.from(this.querySelectorAll('[data-influencer-card]'));
        this.controls = this.querySelector('.influencer-chef__controls');
        this.previousButton = this.querySelector('[data-influencer-previous]');
        this.nextButton = this.querySelector('[data-influencer-next]');
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.previousButton?.addEventListener('click', () => this.move(-1), { signal });
        this.nextButton?.addEventListener('click', () => this.move(1), { signal });
        this.list?.addEventListener('scroll', () => this.requestUpdate(), { passive: true, signal });
        window.addEventListener('resize', () => this.requestUpdate(), { passive: true, signal });

        this.querySelectorAll('[data-influencer-play]').forEach((button) => {
          button.addEventListener('click', () => this.playVideo(button), { signal });
        });

        this.cards.forEach((card, index) => {
          card.addEventListener('click', (event) => {
            if (event.target.closest('a, button, video, iframe')) return;
            this.goTo(index);
          }, { signal });

          card.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            if (event.target.closest('a, button')) return;
            event.preventDefault();
            this.goTo(index);
          }, { signal });
        });

        this.addEventListener('shopify:block:select', (event) => {
          const card = event.target.closest?.('[data-influencer-card]');
          const index = this.cards.indexOf(card);
          if (index >= 0) this.goTo(index, 'auto');
        }, { signal });

        this.update();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        if (this.rafId) cancelAnimationFrame(this.rafId);
      }

      move(direction) {
        this.goTo(this.activeIndex + direction);
      }

      get activeIndex() {
        if (!this.list || !this.cards.length) return 0;
        const listLeft = this.list.getBoundingClientRect().left;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.cards.forEach((card, index) => {
          const distance = Math.abs(card.getBoundingClientRect().left - listLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      goTo(index, behavior) {
        if (!this.list || !this.cards.length) return;
        const targetIndex = Math.max(0, Math.min(index, this.cards.length - 1));
        const target = this.cards[targetIndex];
        if (!target) return;

        this.list.scrollTo({
          left: target.offsetLeft - this.list.offsetLeft,
          behavior: behavior || (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'),
        });
      }

      requestUpdate() {
        if (this.rafId) return;
        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;
          this.update();
        });
      }

      update() {
        if (!this.list) return;
        const maxScroll = Math.max(0, this.list.scrollWidth - this.list.clientWidth);
        const canScroll = maxScroll > 2;
        const activeIndex = this.activeIndex;

        if (this.controls) this.controls.hidden = !canScroll;
        if (this.previousButton) this.previousButton.disabled = !canScroll || this.list.scrollLeft <= 2;
        if (this.nextButton) this.nextButton.disabled = !canScroll || this.list.scrollLeft >= maxScroll - 2;
        this.cards.forEach((card, index) => {
          const selected = index === activeIndex;
          card.classList.toggle('is-selected', selected);
          card.setAttribute('aria-current', selected ? 'true' : 'false');
        });
      }

      playVideo(button) {
        const blockId = button.dataset.influencerPlay;
        const template = this.querySelector(`[data-influencer-video="${CSS.escape(blockId)}"]`);
        const media = button.closest('.influencer-card__media');
        if (!template || !media) return;

        media.replaceChildren(template.content.cloneNode(true));

        const iframe = media.querySelector('iframe');
        if (iframe?.src) {
          const separator = iframe.src.includes('?') ? '&' : '?';
          iframe.src = `${iframe.src}${separator}autoplay=1`;
        }

        media.querySelector('video')?.play().catch(() => {});
      }
    }
  );
}
